/**
 * The usage report: TanStack Query hooks over `GET /metrics/usage` and
 * `GET /metrics/usage/people`.
 *
 * `GET /metrics/usage` holds counts only: no address and no case id. It is for admins
 * (capability `see_usage`, 15 Oct 2026). A session without `see_usage_people` reads the
 * areas it holds, and a figure built from fewer than `min_group` people comes back as
 * null; no role is such a session since 15 Oct 2026. An admin reads
 * every entity and nothing is withheld. `GET /metrics/usage/people` holds one row for
 * each person and is for admins only (capability `see_usage_people`).
 *
 * The page hands over what the address holds (`from`, `to`, `area`, `sim`). A missing
 * day is left to the server: it answers with the last 14 days and names them in
 * `date_from` and `date_to`. The server is the gate; `enabled` only saves a request that
 * would be refused.
 *
 * The shapes come from the generated client, so a backend change shows up at type-check
 * time. This module records nothing: the capture of usage events is `@/lib/usage`.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  getUsageMetrics,
  getUsagePeople,
  type UsagePeopleReport,
  type UsageReport,
} from "./generated";

export type {
  UsageDay,
  UsageDialogRow,
  UsageErrorRow,
  UsageFeatureRow,
  UsageFirstDecisionRow,
  UsageFunnelRow,
  UsageInvestigator,
  UsagePageRow,
  UsagePeopleReport,
  UsagePerson,
  UsageReport,
  UsageTimeRow,
  UsageTotals,
  UsageTrouble,
} from "./generated";

/** What the usage report is asked for. Days are UTC days, both inclusive. */
export interface UsageFilters {
  /** First day (`YYYY-MM-DD`), or null for the server default (13 days before `to`). */
  from: string | null;
  /** Last day (`YYYY-MM-DD`), or null for the server default (today). */
  to: string | null;
  /** Area codes to narrow to, role or entity; empty means the whole scope. */
  areas: readonly string[];
  /** Also count the simulation personas. */
  includeSim: boolean;
}

/** The query keys of the two reports (the areas sorted, so their order is no new key). */
export const usageKeys = {
  all: ["metrics-usage"] as const,
  report: (filters: UsageFilters) =>
    [
      "metrics-usage",
      "report",
      filters.from,
      filters.to,
      [...filters.areas].sort().join(","),
      filters.includeSim,
    ] as const,
  people: (filters: UsageFilters) =>
    [
      "metrics-usage",
      "people",
      filters.from,
      filters.to,
      [...filters.areas].sort().join(","),
      filters.includeSim,
    ] as const,
};

/** The query string of both routes: only what the address holds. */
function usageQuery(filters: UsageFilters) {
  return {
    ...(filters.from ? { from: filters.from } : {}),
    ...(filters.to ? { to: filters.to } : {}),
    ...(filters.areas.length > 0 ? { area: [...filters.areas] } : {}),
    ...(filters.includeSim ? { include_sim: true } : {}),
  };
}

const STALE_MS = 5 * 60_000;

/** The aggregates of the usage log (admins). */
export function useUsageMetrics(filters: UsageFilters, enabled: boolean) {
  return useQuery({
    queryKey: usageKeys.report(filters),
    queryFn: async (): Promise<UsageReport | null> => {
      const { data } = await getUsageMetrics({ query: usageQuery(filters), throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}

/** The use of each person (admins only). */
export function useUsagePeople(filters: UsageFilters, enabled: boolean) {
  return useQuery({
    queryKey: usageKeys.people(filters),
    queryFn: async (): Promise<UsagePeopleReport | null> => {
      const { data } = await getUsagePeople({ query: usageQuery(filters), throwOnError: true });
      return data ?? null;
    },
    enabled,
    retry: false,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
  });
}
